import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { captureUtmFromUrl } from "@/lib/utm";
import CookieConsent from "@/components/app/CookieConsent";
import AccessibilityWidget from "@/components/app/AccessibilityWidget";

import LandingPage from "@/pages/LandingPage";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import LegalPage from "@/pages/LegalPage";
import FunnelGuide1 from "@/pages/FunnelGuide1";
import FunnelGuide2 from "@/pages/FunnelGuide2";
import FunnelGuide3 from "@/pages/FunnelGuide3";
import FunnelGuide4 from "@/pages/FunnelGuide4";
import AdminLogin from "@/pages/admin/Login";
import AuthCallback from "@/pages/admin/AuthCallback";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminCampaigns from "@/pages/admin/Campaigns";
import AdminCampaignBuilder from "@/pages/admin/CampaignBuilder";
import AdminAffiliates from "@/pages/admin/Affiliates";
import AdminRevshareImport from "@/pages/admin/RevshareImport";
import AdminLeads from "@/pages/admin/Leads";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminEmails from "@/pages/admin/Emails";
import AdminEmailEditor from "@/pages/admin/EmailEditor";
import AdminDrips from "@/pages/admin/Drips";
import AdminDripEditor from "@/pages/admin/DripEditor";
import AdminBroadcasts from "@/pages/admin/Broadcasts";
import AdminSuppressions from "@/pages/admin/Suppressions";
import AdminQuestionnaireResponses from "@/pages/admin/QuestionnaireResponses";
import AffiliateReports from "@/pages/admin/AffiliateReports";
import AdminVOD from "@/pages/admin/VOD";
import VODSignup from "@/pages/VODSignup";
import Questionnaire from "@/pages/Questionnaire";
import AffiliatePitch from "@/pages/AffiliatePitch";
import ShortLinkRedirect from "@/pages/ShortLinkRedirect";
import AdminShortLinks from "@/pages/admin/ShortLinks";
import AdminWhatsApp from "@/pages/admin/WhatsApp";
import MagicLogin from "@/pages/community/MagicLogin";
import AccessDenied from "@/pages/community/AccessDenied";
import CommunityHome from "@/pages/community/CommunityHome";
import WatchPage from "@/pages/community/WatchPage";
import Calculators from "@/pages/community/Calculators";
import Account from "@/pages/community/Account";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect session_id in URL fragment synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/c/:slug" element={<LandingPage />} />
        <Route path="/affiliate" element={<AffiliateDashboard />} />
        <Route path="/privacy" element={<LegalPage docType="privacy" />} />
        <Route path="/terms" element={<LegalPage docType="terms" />} />
        <Route path="/contact" element={<LegalPage docType="contact" />} />
        <Route path="/c/:slug/privacy" element={<LegalPage docType="privacy" />} />
        <Route path="/c/:slug/terms" element={<LegalPage docType="terms" />} />
        <Route path="/c/:slug/contact" element={<LegalPage docType="contact" />} />
        <Route path="/funnel/:leadId/1" element={<FunnelGuide1 />} />
        <Route path="/funnel/:leadId/2" element={<FunnelGuide2 />} />
        <Route path="/funnel/:leadId/3" element={<FunnelGuide3 />} />
        <Route path="/funnel/:leadId/4" element={<FunnelGuide4 />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/campaigns" element={<AdminCampaigns />} />
        <Route path="/admin/campaigns/new" element={<AdminCampaignBuilder />} />
        <Route path="/admin/campaigns/:id" element={<AdminCampaignBuilder />} />
        <Route path="/admin/affiliates" element={<AdminAffiliates />} />
        <Route path="/admin/revshare-import" element={<AdminRevshareImport />} />
        <Route path="/admin/leads" element={<AdminLeads />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/analytics/:campaignId" element={<AdminAnalytics />} />
        <Route path="/admin/emails" element={<AdminEmails />} />
        <Route path="/admin/emails/new" element={<AdminEmailEditor />} />
        <Route path="/admin/emails/:id" element={<AdminEmailEditor />} />
        <Route path="/admin/drips" element={<AdminDrips />} />
        <Route path="/admin/drips/new" element={<AdminDripEditor />} />
        <Route path="/admin/drips/:id" element={<AdminDripEditor />} />
        <Route path="/admin/broadcasts" element={<AdminBroadcasts />} />
        <Route path="/admin/suppressions" element={<AdminSuppressions />} />
        <Route path="/admin/questionnaire-responses" element={<AdminQuestionnaireResponses />} />
        <Route path="/admin/reports" element={<AffiliateReports />} />
        <Route path="/admin/vod" element={<AdminVOD />} />
        <Route path="/admin/short-links" element={<AdminShortLinks />} />
        <Route path="/admin/whatsapp" element={<AdminWhatsApp />} />
        {/* VOD Squeeze Page (Funnel B) */}
        <Route path="/vod" element={<VODSignup />} />
        {/* Interactive financial questionnaire (post-signup thank-you page) */}
        <Route path="/q/:campaignSlug/:leadId" element={<Questionnaire />} />
        {/* Post-signup affiliate pitch page (thank-you variant) */}
        <Route path="/pitch/:leadId" element={<AffiliatePitch />} />
        {/* Public short-link resolver */}
        <Route path="/s/:slug" element={<ShortLinkRedirect />} />
        {/* Community (member) area */}
        <Route path="/magic-login" element={<MagicLogin />} />
        <Route path="/community/access-denied" element={<AccessDenied />} />
        <Route path="/community" element={<CommunityHome />} />
        <Route path="/community/watch/:videoId" element={<WatchPage />} />
        <Route path="/community/calculators" element={<Calculators />} />
        <Route path="/community/account" element={<Account />} />
      </Routes>
      {!isAdmin && <CookieConsent />}
      {!isAdmin && <AccessibilityWidget />}
    </>
  );
}

function App() {
  useEffect(() => {
    captureUtmFromUrl();
  }, []);

  return (
    <div className="App font-sans" dir="rtl">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default App;
